import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { AdminLayout } from "../shared/layout";
import { getUser, createUser, updateUser, getRoles } from "../../../data";
import { getUserTemplates, getAllTemplates, assignTemplateToUser, removeTemplateFromUser } from "../../../data/requests/template-requests";
import { useNavigate, useParams } from "react-router-dom";
import { CardBox } from "../shared/CardBox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import { Trash2, Plus } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

export function UserManageScreen() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isEditMode = !!id;
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const { data: userData, isLoading: isUserLoading } = useQuery({
    queryKey: ["user", id],
    queryFn: () => getUser(id),
    enabled: isEditMode,
  });

  const { data: rolesData, isLoading: isRolesLoading, error: rolesError } = useQuery({
    queryKey: ["roles"],
    queryFn: getRoles,
  });

  const { data: userTemplatesData, refetch: refetchUserTemplates } = useQuery({
    queryKey: ["userTemplates", id],
    queryFn: () => getUserTemplates(id).then(res => res.data),
    enabled: isEditMode,
  });

  const { data: allTemplatesData } = useQuery({
    queryKey: ["allTemplates"],
    queryFn: () => getAllTemplates().then(res => res.data),
    enabled: isEditMode && templateDialogOpen,
  });

  const assignTemplateMutation = useMutation({
    mutationFn: ({ templateId }) => assignTemplateToUser(id, templateId),
    onSuccess: () => {
      refetchUserTemplates();
      setTemplateDialogOpen(false);
      toast({
        title: "Başarılı",
        description: "Template kullanıcıya atandı",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Template atanamadı",
        variant: "destructive",
      });
    },
  });

  const removeTemplateMutation = useMutation({
    mutationFn: (templateId) => removeTemplateFromUser(id, templateId),
    onSuccess: () => {
      refetchUserTemplates();
      toast({
        title: "Başarılı",
        description: "Template kullanıcıdan kaldırıldı",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: error.response?.data?.message || "Template kaldırılamadı",
        variant: "destructive",
      });
    },
  });

  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: React.useMemo(() => ({
      name: userData?.data?.name || "",
      email: userData?.data?.email || "",
      status: userData?.data?.status || "ACTIVE",
      roles: userData?.data?.roles?.map(r => r.roleId.toString()) || [],
      webhookUrl: userData?.data?.webhookUrl || "",
      webhookSecret: userData?.data?.webhookSecret || "",
      webhookEnabled: userData?.data?.webhookEnabled || false,
      postifyTemplateId: userData?.data?.postifyTemplateId || "",
      brandingLogo: userData?.data?.brandingLogo || "",
      brandingPrimaryColor: userData?.data?.brandingPrimaryColor || "#000000",
      brandingSecondaryColor: userData?.data?.brandingSecondaryColor || "#FFFFFF",
    }), [userData?.data])
  });

  React.useEffect(() => {
    if (userData?.data) {
      reset({
        name: userData.data.name,
        email: userData.data.email,
        status: userData.data.status || "ACTIVE",
        roles: userData.data.roles.map(r => r.roleId.toString()),
        webhookUrl: userData.data.webhookUrl || "",
        webhookSecret: userData.data.webhookSecret || "",
        webhookEnabled: userData.data.webhookEnabled || false,
        postifyTemplateId: userData.data.postifyTemplateId || "",
        brandingLogo: userData.data.brandingLogo || "",
        brandingPrimaryColor: userData.data.brandingPrimaryColor || "#000000",
        brandingSecondaryColor: userData.data.brandingSecondaryColor || "#FFFFFF",
      });
    }
  }, [userData?.data, reset]);

  const mutation = useMutation({
    mutationFn: (data) => isEditMode ? updateUser(id, data) : createUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      toast({
        title: "Başarılı",
        description: isEditMode ? "Kullanıcı güncellendi" : "Kullanıcı oluşturuldu",
        variant: "success",
      });
      navigate("/admin/users");
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Bir hata oluştu",
        variant: "destructive",
      });
      console.error("Kullanıcı işlem hatası:", error);
    },
  });

  const onSubmit = (data) => {
    const formattedData = {
      ...data,
      roles: Array.isArray(data.roles) ? data.roles.map(roleId => parseInt(roleId, 10)) : [],
    };
    
    // Only include newPassword if it's filled out
    if (isEditMode && data.newPassword && data.newPassword.trim() !== '') {
      formattedData.password = data.newPassword;
    }
    
    // Remove newPassword field as server expects 'password'
    delete formattedData.newPassword;
    
    mutation.mutate(formattedData);
  };

  if ((isUserLoading && isEditMode) || isRolesLoading) {
    return <AdminLayout title="Kullanıcı Yönetimi">Yükleniyor...</AdminLayout>;
  }

  return (
    <AdminLayout title={isEditMode ? "Kullanıcı Güncelle" : "Yeni Kullanıcı Ekle"}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        <CardBox>
          <h2 className="text-2xl font-bold mb-4">Kullanıcı Bilgileri</h2>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">İsim</Label>
              <Controller
                name="name"
                control={control}
                rules={{ required: "İsim gereklidir" }}
                render={({ field }) => <Input id="name" {...field} />}
              />
              {errors.name && <p className="text-red-500">{errors.name.message}</p>}
            </div>

            <div>
              <Label htmlFor="email">E-posta</Label>
              <Controller
                name="email"
                control={control}
                rules={{ required: "E-posta gereklidir", pattern: { value: /^\S+@\S+$/i, message: "Geçerli bir e-posta adresi giriniz" } }}
                render={({ field }) => <Input id="email" type="email" {...field} />}
              />
              {errors.email && <p className="text-red-500">{errors.email.message}</p>}
            </div>

            {!isEditMode && (
              <div>
                <Label htmlFor="password">Şifre</Label>
                <Controller
                  name="password"
                  control={control}
                  rules={{ required: "Şifre gereklidir", minLength: { value: 6, message: "Şifre en az 6 karakter olmalıdır" } }}
                  render={({ field }) => <Input id="password" type="password" {...field} />}
                />
                {errors.password && <p className="text-red-500">{errors.password.message}</p>}
              </div>
            )}

            {isEditMode && (
              <div>
                <Label htmlFor="newPassword">Yeni Şifre (Boş bırakırsanız değişmez)</Label>
                <Controller
                  name="newPassword"
                  control={control}
                  rules={{ 
                    minLength: { value: 6, message: "Şifre en az 6 karakter olmalıdır" }
                  }}
                  render={({ field }) => <Input id="newPassword" type="password" placeholder="Yeni şifre (opsiyonel)" {...field} />}
                />
                {errors.newPassword && <p className="text-red-500">{errors.newPassword.message}</p>}
                <p className="text-sm text-gray-500 mt-1">
                  Şifreyi değiştirmek istemiyorsanız boş bırakabilirsiniz
                </p>
              </div>
            )}

            {isEditMode && (
              <div>
                <Label htmlFor="status">Durum</Label>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue placeholder="Durum seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ACTIVE">Aktif</SelectItem>
                        <SelectItem value="INACTIVE">Pasif</SelectItem>
                        <SelectItem value="SUSPENDED">Askıda</SelectItem>
                        <SelectItem value="BANNED">Yasaklı</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
            )}

            <div>
              <Label>Roller</Label>
              <div className="space-y-2">
                {rolesData?.data?.map((role) => (
                  <div key={role.id} className="flex items-center">
                    <Controller
                      name="roles"
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          id={`role-${role.id}`}
                          checked={field.value?.includes(role.id.toString())}
                          onCheckedChange={(checked) => {
                            const updatedRoles = checked
                              ? [...(field.value || []), role.id.toString()]
                              : field.value?.filter((id) => id !== role.id.toString()) || [];
                            field.onChange(updatedRoles);
                          }}
                        />
                      )}
                    />
                    <Label htmlFor={`role-${role.id}`} className="ml-2">
                      {role.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardBox>

        {isEditMode && (
          <>
            <CardBox>
              <h2 className="text-2xl font-bold mb-4">AI Booth - Webhook Ayarları</h2>
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Controller
                    name="webhookEnabled"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        id="webhookEnabled"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <Label htmlFor="webhookEnabled">Webhook Aktif</Label>
                </div>

                <div>
                  <Label htmlFor="webhookUrl">Webhook URL</Label>
                  <Controller
                    name="webhookUrl"
                    control={control}
                    render={({ field }) => (
                      <Input 
                        id="webhookUrl" 
                        type="url"
                        placeholder="https://api.example.com/process"
                        {...field} 
                      />
                    )}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    AI işleme isteklerinin gönderileceği endpoint
                  </p>
                </div>

                <div>
                  <Label htmlFor="webhookSecret">Webhook Secret</Label>
                  <Controller
                    name="webhookSecret"
                    control={control}
                    render={({ field }) => (
                      <Input 
                        id="webhookSecret" 
                        type="password"
                        placeholder="HMAC imzalama için secret key"
                        {...field} 
                      />
                    )}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Webhook isteklerini imzalamak için kullanılır (HMAC-SHA256)
                  </p>
                </div>
              </div>
            </CardBox>

            <CardBox>
              <h2 className="text-2xl font-bold mb-4">Postify Ayarları</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="postifyTemplateId">Postify Template ID</Label>
                  <Controller
                    name="postifyTemplateId"
                    control={control}
                    render={({ field }) => (
                      <Input 
                        id="postifyTemplateId" 
                        type="text"
                        placeholder="Postify template ID (örn: 684bc47e7c6e441cb7e09b2a)"
                        {...field} 
                      />
                    )}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Postify AI servisine webhook gönderilirken kullanılacak template ID. 
                    Bu ID, Postify panelinden alınır.
                  </p>
                </div>
              </div>
            </CardBox>

            <CardBox>
              <h2 className="text-2xl font-bold mb-4">AI Booth - Branding Ayarları</h2>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="brandingLogo">Logo URL</Label>
                  <Controller
                    name="brandingLogo"
                    control={control}
                    render={({ field }) => (
                      <Input 
                        id="brandingLogo" 
                        type="url"
                        placeholder="https://example.com/logo.png"
                        {...field} 
                      />
                    )}
                  />
                  {userData?.data?.brandingLogo && (
                    <img 
                      src={userData.data.brandingLogo} 
                      alt="Logo" 
                      className="mt-2 w-32 h-32 object-contain border rounded"
                    />
                  )}
                </div>

                <div>
                  <Label htmlFor="brandingPrimaryColor">Ana Renk</Label>
                  <div className="flex gap-2">
                    <Controller
                      name="brandingPrimaryColor"
                      control={control}
                      render={({ field }) => (
                        <>
                          <Input 
                            type="color" 
                            {...field} 
                            className="w-20 h-10"
                          />
                          <Input 
                            type="text" 
                            value={field.value}
                            onChange={field.onChange}
                            className="flex-1"
                          />
                        </>
                      )}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="brandingSecondaryColor">İkincil Renk</Label>
                  <div className="flex gap-2">
                    <Controller
                      name="brandingSecondaryColor"
                      control={control}
                      render={({ field }) => (
                        <>
                          <Input 
                            type="color" 
                            {...field} 
                            className="w-20 h-10"
                          />
                          <Input 
                            type="text" 
                            value={field.value}
                            onChange={field.onChange}
                            className="flex-1"
                          />
                        </>
                      )}
                    />
                  </div>
                </div>

                <div className="mt-4 p-4 border rounded" style={{
                  backgroundColor: userData?.data?.brandingSecondaryColor || '#FFFFFF',
                  borderColor: userData?.data?.brandingPrimaryColor || '#000000',
                }}>
                  <p style={{ color: userData?.data?.brandingPrimaryColor || '#000000' }} className="font-bold">
                    Önizleme
                  </p>
                  <p className="text-sm text-gray-600">Download sayfasında böyle görünecek</p>
                </div>
              </div>
            </CardBox>

            <CardBox>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">AI Booth - Kullanıcı Template'leri</h2>
                <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
                  <DialogTrigger asChild>
                    <Button type="button" variant="outline">
                      <Plus className="mr-2 h-4 w-4" />
                      Template Ekle
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Template Seç</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                      {allTemplatesData?.templates?.map((template) => {
                        const isAssigned = userTemplatesData?.userTemplates?.some(
                          ut => ut.template.id === template.id
                        );
                        return (
                          <div
                            key={template.id}
                            className={`border rounded-lg p-4 cursor-pointer transition-all ${
                              isAssigned ? "opacity-50 cursor-not-allowed" : "hover:border-blue-500"
                            }`}
                            onClick={() => !isAssigned && assignTemplateMutation.mutate({ templateId: template.id })}
                          >
                            {template.thumbnailUrl && (
                              <img
                                src={template.thumbnailUrl}
                                alt={template.name}
                                className="w-full h-32 object-cover rounded mb-2"
                              />
                            )}
                            <h3 className="font-semibold">{template.name}</h3>
                            {template.category && (
                              <Badge variant="secondary" className="mt-2">{template.category}</Badge>
                            )}
                            {isAssigned && (
                              <Badge variant="default" className="mt-2 ml-2">Atanmış</Badge>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {userTemplatesData?.userTemplates?.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  Henüz template atanmamış
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {userTemplatesData?.userTemplates?.map((ut) => (
                    <div
                      key={ut.id}
                      className="border rounded-lg p-4 relative"
                    >
                      {ut.template.thumbnailUrl && (
                        <img
                          src={ut.template.thumbnailUrl}
                          alt={ut.template.name}
                          className="w-full h-32 object-cover rounded mb-2"
                        />
                      )}
                      <h3 className="font-semibold">{ut.template.name}</h3>
                      {ut.template.category && (
                        <Badge variant="secondary" className="mt-2">{ut.template.category}</Badge>
                      )}
                      {ut.isActive && (
                        <Badge variant="success" className="mt-2 ml-2">Aktif</Badge>
                      )}
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => {
                          if (window.confirm("Bu template'i kullanıcıdan kaldırmak istediğinize emin misiniz?")) {
                            removeTemplateMutation.mutate(ut.template.id);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardBox>
          </>
        )}
        
        <Button type="submit" className="w-full">
          {isEditMode ? "Kullanıcı Güncelle" : "Kullanıcı Oluştur"}
        </Button>
      </form>
    </AdminLayout>
  );
}
